import {AttrsSchema, Component} from 'panel';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {promisifyModal} from 'common/components/modal/utils';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {ReportName} from 'common/report/constants';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {encodeRison} from 'common/report/util/url';

import {CohortDetail} from '../create-cohort-button';
import {TabOptions, UserSidebarJsxProps} from './types';
import {template} from './template';
import './index.styl';
import {TrackingEvent} from './tracking';

export async function openUserSidebar(
  attrs: UserSidebarJsxProps['attrs'],
  props: UserSidebarJsxProps['props'],
  handlers: UserSidebarJsxProps['handlers'],
): Promise<CustomEvent<any>> {
  handlers.onOpen();
  const closeEvents: string[] = [`close`, `stackable-closed`];
  const closeEv = await promisifyModal({
    tagName: `mp-user-sidebar`,
    attrs,
    props,
    closeEvents,
  });
  if (closeEvents.includes(closeEv.type)) {
    handlers.onClose();
  }
  return closeEv;
}

export class UsersSidebar extends Component<
  UserSidebarJsxProps['state'],
  UserSidebarJsxProps['attrs'],
  EmptyObj,
  void,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<UserSidebarJsxProps['attrs']> {
    return {
      'hide-cohort-functionality': `boolean`,
      'include-all-users': `boolean`,
      'from-date': `string`,
      'to-date': `string`,
      'report-name': {
        type: `string`,
        enum: [ReportName.Insights, ReportName.Flows, ReportName.Funnels, ReportName.Retention],
      },
      segments: `json`,
      color: {type: `string`, default: `var(--component-outline-color)`},
      'segment-label-key': `string`,
      'data-group-name': {type: `string`, default: `user`},
    };
  }

  handleUserSelected = (ev: CustomEvent<{distinctID: string}>) => {
    mixpanel.track(TrackingEvent.ViewUserDetail, {
      'User ID': ev.detail.distinctID,
    });
    this.update({
      selectedTab: TabOptions.USER_DETAIL,
      distinctID: ev.detail.distinctID,
    });
  };

  handleBack = () => {
    this.update({
      distinctID: ``,
      selectedTab: TabOptions.USER_LIST,
    });
  };

  get config() {
    return {
      template,
      contexts: [`reportLayoutManager` as const, `i18n` as const],
      defaultState: {
        // Serves as a state for controlling actual widget to be rendered in the sidebar
        selectedTab: TabOptions.USER_LIST,
        distinctID: ``,
        cohortDetails: [],
        cohort: new Cohort(),
      },
      helpers: {
        getWidth: () => this.getContext(`reportLayoutManager`).getUserSidebarWidth(),
      },
    };
  }

  set cohort(cohort: Cohort) {
    this.update({
      cohort,
    });
  }

  set cohortDetails(cohortDetails: CohortDetail[]) {
    this.update({
      cohortDetails,
    });
  }

  handleClose = () => {
    this.dispatchEvent(new CustomEvent(`close`));
  };

  handleTabChange = (ev: CustomEvent<{selectedTab: string}>) => {
    mixpanel.track(TrackingEvent.ProfileTabChange, {
      'Tab Name': ev.detail.selectedTab,
    });
  };

  getUserProfilePageUrl = () => {
    let url = `${getReportUrl(`profile`, {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    })}#distinct_id=${encodeURIComponent(this.state.distinctID)}`;
    if (this.state.cohort.dataGroupId) {
      url = `${url}&data_group_id=${this.state.cohort.dataGroupId}`;
    }
    return url;
  };

  getUsersPageUrl = () => {
    const params = {
      include_all_users: this.attr(`include-all-users`),
      serialized_cohort: encodeURIComponent(JSON.stringify(this.state.cohort.serialize())),
      query_source: `View Users`,
      ...(this.state.cohort.dataGroupId
        ? {
            data_group_id: this.state.cohort.dataGroupId,
          }
        : {}),
    };
    const url =
      getReportUrl(ReportName.Users, {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      }) + `#list/${encodeRison(params)}`;
    return url;
  };

  trackExpandUsersPage() {
    mixpanel.track(TrackingEvent.ExpandUsers);
  }

  trackExpandProfilePage() {
    mixpanel.track(TrackingEvent.ExpandProfile, {
      'Tab Name': this.state.selectedTab,
    });
  }
}

export const UsersSidebarEl = defineAndMakeJsxFactory<UserSidebarJsxProps>(`mp-user-sidebar`, UsersSidebar);
export const UsersSidebarReact = panel2react<UserSidebarJsxProps>(`mp-user-sidebar`, UsersSidebar);
